import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { normalizePaginatedResponse, posApi, productsApi, storesApi } from '../services/api';
import BarcodeScannerModal from '../components/pos/BarcodeScannerModal';

export default function PosPage() {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [query, setQuery] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [processing, setProcessing] = useState(false);
    const [taxRate, setTaxRate] = useState(0);
    const [loyaltyConfig, setLoyaltyConfig] = useState(null);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scanMessage, setScanMessage] = useState('');
    const hardwareBufferRef = useRef('');
    const hardwareTimerRef = useRef(null);

    useEffect(() => {
        productsApi.list({ limit: 100 }).then((res) => {
            if (!res.success) return;
            const normalized = normalizePaginatedResponse(res);
            setProducts(normalized.rows);
        });

        posApi.getLoyaltyConfig().then((res) => {
            if (res.success) setLoyaltyConfig(res.data);
        });

        storesApi.list({ limit: 200 }).then((res) => {
            if (!res.success) return;
            const list = res?.data?.data || [];
            const selectedStore = list.find((item) => String(item.id) === String(user?.storeId)) || list[0];
            if (selectedStore) setTaxRate(Number(selectedStore.tax_rate || 0));
        });
    }, [user?.storeId]);

    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        return products.filter(
            (p) =>
                !q ||
                p.name?.toLowerCase().includes(q) ||
                p.sku?.toLowerCase().includes(q) ||
                p.barcode?.includes(q)
        );
    }, [products, query]);

    const addToCart = useCallback((p) => {
        setCart((prev) => {
            const idx = prev.findIndex((i) => i.product_id === p.id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx].quantity += 1;
                return copy;
            }
            return [...prev, { product_id: p.id, name: p.name, price: Number(p.selling_price), quantity: 1 }];
        });
    }, []);

    const handleScannedCode = useCallback((rawCode) => {
        const code = String(rawCode || '').trim();
        if (!code) return false;

        const product = products.find((p) => {
            const barcode = String(p.barcode || '').trim();
            const sku = String(p.sku || '').trim();
            return barcode === code || sku === code || String(p.id) === code;
        });

        if (!product) {
            setQuery(code);
            setScanMessage(`No exact product found for ${code}.`);
            return false;
        }

        if (Number(product.quantity_in_stock || 0) <= 0) {
            setScanMessage(`${product.name} is out of stock.`);
            return false;
        }

        addToCart(product);
        setQuery('');
        setScanMessage(`${product.name} added to cart.`);
        return true;
    }, [addToCart, products]);

    const handleSearchKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        if (handleScannedCode(query)) {
            event.preventDefault();
        }
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            const target = event.target;
            const tagName = String(target?.tagName || '').toLowerCase();
            const isTypingField = ['input', 'textarea', 'select'].includes(tagName) || target?.isContentEditable;

            if (isTypingField || event.ctrlKey || event.metaKey || event.altKey) return;

            if (event.key === 'Enter') {
                if (hardwareBufferRef.current) {
                    handleScannedCode(hardwareBufferRef.current);
                    hardwareBufferRef.current = '';
                    event.preventDefault();
                }
                return;
            }

            if (event.key.length !== 1) return;
            hardwareBufferRef.current += event.key;
            clearTimeout(hardwareTimerRef.current);
            hardwareTimerRef.current = setTimeout(() => {
                hardwareBufferRef.current = '';
            }, 120);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            clearTimeout(hardwareTimerRef.current);
        };
    }, [handleScannedCode]);

    const updateQty = (id, qty) => {
        setCart((prev) =>
            prev
                .map((i) => (i.product_id === id ? { ...i, quantity: qty } : i))
                .filter((i) => i.quantity > 0)
        );
    };

    const subtotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const taxAmount = (subtotal * Number(taxRate || 0)) / 100;
    const total = subtotal + taxAmount;
    const loyaltyPointsEarned = loyaltyConfig?.enabled
        ? Math.floor(subtotal * Number(loyaltyConfig.points_per_currency || 0))
        : 0;

    const checkout = async () => {
        if (!cart.length) return;
        setProcessing(true);
        const payload = {
            items: cart.map(({ product_id, quantity, price }) => ({ product_id, quantity, price })),
            payment_method: paymentMethod,
            total_amount: total,
            tax_amount: taxAmount,
            discount_amount: 0,
        };
        const res = await posApi.createTransaction(payload);
        setProcessing(false);
        if (res.success) {
            alert(`Sale completed. Receipt: ${res.data.receipt_number || ''}`);
            setCart([]);
        } else {
            alert(typeof res.error === 'string' ? res.error : 'Checkout failed');
        }
    };

    return (
        <div className="pos-grid">
            <section className="panel">
                <div className="panel-header">
                    <h3>Products</h3>
                    <input
                        placeholder="Scan barcode or search name/SKU/barcode"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                    <button className="btn btn-outline" type="button" onClick={() => setScannerOpen(true)}>Camera Scan</button>
                </div>
                {scanMessage ? <p className="scanner-inline-status">{scanMessage}</p> : null}

                <div className="products-grid">
                    {filtered.map((p) => (
                        <button key={p.id} className="product-card" onClick={() => addToCart(p)}>
                            <h4>{p.name}</h4>
                            <p>₦{Number(p.selling_price).toLocaleString()}</p>
                            <small>Stock: {Number(p.quantity_in_stock || 0)}</small>
                        </button>
                    ))}
                </div>
            </section>

            <section className="panel">
                <div className="panel-header"><h3>Cart</h3></div>

                <div className="cart-list">
                    {cart.map((item) => (
                        <div className="cart-item" key={item.product_id}>
                            <div>
                                <strong>{item.name}</strong>
                                <p>₦{item.price.toLocaleString()}</p>
                            </div>
                            <div className="qty-control">
                                <button onClick={() => updateQty(item.product_id, item.quantity - 1)}>-</button>
                                <span>{item.quantity}</span>
                                <button onClick={() => updateQty(item.product_id, item.quantity + 1)}>+</button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="checkout-bar">
                    <label>Payment Method</label>
                    <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                        <option value="cash">Cash</option>
                        <option value="transfer">Transfer</option>
                        <option value="card">Card</option>
                    </select>
                    <p style={{ margin: '6px 0 0', color: 'var(--muted)' }}>Subtotal: ₦{subtotal.toLocaleString()}</p>
                    <p style={{ margin: '4px 0 0', color: 'var(--muted)' }}>Tax ({Number(taxRate || 0).toFixed(2)}%): ₦{taxAmount.toLocaleString()}</p>
                    <p style={{ margin: '4px 0 0', color: 'var(--muted)' }}>Loyalty Points to Earn: {loyaltyPointsEarned}</p>
                    <h2>Total: ₦{total.toLocaleString()}</h2>
                    <button className="btn" onClick={checkout} disabled={!cart.length || processing}>
                        {processing ? 'Processing...' : 'Checkout'}
                    </button>
                </div>
            </section>
            {scannerOpen ? (
                <BarcodeScannerModal
                    onClose={() => setScannerOpen(false)}
                    onScan={(code) => {
                        handleScannedCode(code);
                        setScannerOpen(false);
                    }}
                />
            ) : null}
        </div>
    );
}
